import {
  READY_PLACEHOLDERS, WEEKDAYS, buildReadySms, countedLines, fillTemplate, formatOpeningHours, formatPlate, formatReadyAmount, noticeOpeningHours,
  noticeShowsAmount, readyAmount, readyValues, serializeOpeningHours, siteAddressOf, smsSegments, type ReadyValues,
} from "@szerviz/core";
import { displayPhone, smsWithPhone } from "../../../../supabase/functions/_shared/messaging";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
type TFunction = (key: string, options?: Record<string, unknown>) => string;
import { useApp } from "../context";
import type { Customer, ReadyNotice, WorkOrder, WorkOrderItem } from "../data";
import { ChannelSend } from "./ChannelSend";
import { Help } from "./Help";

/** The customer's page of a notice: short, because it goes in an SMS. */
export const readyLink = (token: string) => `${globalThis.location?.origin ?? ""}${globalThis.location?.pathname ?? "/"}#ready/${token}`;
/** Stands in for the token before the notice exists (previews). */
const SAMPLE_TOKEN = "0".repeat(24);

/** A template of the locale with its placeholders kept (i18next would fill them in). */
export function defaultTemplate(t: TFunction, key: "readyNotice.defaultEmail" | "readyNotice.defaultSms"): string {
  return t(key, Object.fromEntries(READY_PLACEHOLDERS.map((k) => [k, `{{${k}}}`])));
}

/** Short day names, Monday first. */
export const dayNames = (t: TFunction) => WEEKDAYS.map((d) => t(`readyNotice.day.${d}`));

export interface ReadyTexts {
  subject: string;
  email: string;
  sms: string;
  smsParts: number;
  smsPlain: boolean;
  amount: number | null;
  hours: string | null;
}

/** The two texts of the notice, from the workshop's templates (or the language's) and the work order. */
export function useReadyTexts() {
  const { t, i18n } = useTranslation();
  const { workshop } = useApp();
  return useCallback(
    (input: { order: Pick<WorkOrder, "number" | "plateNumber" | "plateCountry" | "vehicleLabel">; customerName: string | null; gross: number; token: string | null; general?: Record<string, unknown> }): ReadyTexts => {
      const g = input.general ?? workshop.general;
      const amount = noticeShowsAmount(g) ? readyAmount(input.gross, workshop.currency) : null;
      const hours = noticeOpeningHours(g);
      const plate = input.order.plateNumber ? formatPlate(input.order.plateNumber, input.order.plateCountry ?? "HU") : null;
      const str = (k: string) => (typeof g[k] === "string" && (g[k] as string).trim() ? (g[k] as string) : null);
      const values: ReadyValues = readyValues({
        customerName: input.customerName, fallbackName: t("readyNotice.fallbackName"), plate, vehicle: input.order.vehicleLabel ?? null, number: input.order.number,
        amountText: amount === null ? null : t("readyNotice.approx", { amount: formatReadyAmount(amount, workshop.currency, i18n.language) }),
        hoursText: hours ? formatOpeningHours(hours, dayNames(t)) : null, address: siteAddressOf(g),
        link: readyLink(input.token ?? SAMPLE_TOKEN), workshop: workshop.name, phone: displayPhone(str("workshopPhone")) ?? str("workshopPhone"),
      });
      const built = buildReadySms(str("readyNoticeSms") ?? defaultTemplate(t, "readyNotice.defaultSms"), values);
      // the SMS always carries the workshop's number in the "+36 ..." form phones link (send-quote adds it the same way)
      const smsBody = smsWithPhone(built.text, str("workshopPhone"), t("contacts.smsPhone", { phone: "{{phone}}" }));
      const sms = { text: smsBody, segments: smsSegments(smsBody).segments, plain: built.plain };
      return {
        subject: t("readyNotice.emailSubject", { plate: values.plate ?? "" }),
        email: fillTemplate(str("readyNoticeEmail") ?? defaultTemplate(t, "readyNotice.defaultEmail"), values),
        sms: sms.text, smsParts: sms.segments, smsPlain: sms.plain,
        amount, hours: hours ? serializeOpeningHours(hours) : null,
      };
    },
    [t, i18n.language, workshop],
  );
}

/** When and where the notices of a work order went (newest first). */
export function ReadyNoticeList({ notices }: { notices: ReadyNotice[] }) {
  const { t, i18n } = useTranslation();
  const sent = notices.filter((n) => n.channels.length > 0);
  if (sent.length === 0) return null;
  const when = (iso: string) => new Intl.DateTimeFormat(i18n.language, { dateStyle: "medium", timeStyle: "short" }).format(new Date(iso));
  return (
    <div className="ready-sent">
      <strong>{t("readyNotice.sentList")}</strong>
      <ul>
        {sent.map((n) => (
          <li key={n.id}>
            {when(n.createdAt)} ·{" "}
            {n.channels.map((c) => `${t(`channels.${c}`)}${c === "email" && n.emailTo ? ` (${n.emailTo})` : c === "sms" && n.smsTo ? ` (${n.smsTo})` : ""}`).join(", ")}
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * "Elkészült az autója" on the work order: the offer right after the status became ready, the button on a work order
 * in any status (open, ready or closed), the send panel with the channel ticks and the texts, and the record of what went out.
 */
export function ReadyNoticeBox({ order, items, customer, offer, onOfferDone }: {
  order: WorkOrder;
  items: WorkOrderItem[];
  customer: Customer | null;
  /** Set right after the work order was set to ready: asks once. */
  offer: boolean;
  onOfferDone: () => void;
}) {
  const { t, i18n } = useTranslation();
  const { store, workshop } = useApp();
  const texts = useReadyTexts();
  const [open, setOpen] = useState(false);
  const [notices, setNotices] = useState<ReadyNotice[]>([]);
  const load = useCallback(() => store.readyNotices(order.id).then(setNotices, () => setNotices([])), [store, order.id]);
  useEffect(() => void load(), [load]);
  if (!order.customerId) return null;

  const gross = countedLines(items).reduce((a, i) => a + i.gross, 0);
  const preview = texts({ order, customerName: customer?.name ?? null, gross, token: null });
  const noAddress = !siteAddressOf(workshop.general);
  return (
    <section className="ready-box no-print" aria-label={t("readyNotice.title")}>
      {offer && !open && (
        <div className="ready-offer" role="status">
          <strong>{t("readyNotice.offerTitle")}</strong>
          <div className="actions">
            <button type="button" className="primary" onClick={() => { setOpen(true); onOfferDone(); }}>
              {t("readyNotice.offerYes")}
            </button>
            <button type="button" onClick={onOfferDone}>{t("readyNotice.offerNo")}</button>
          </div>
        </div>
      )}
      {!offer && !open && (
        <button type="button" className="ready-open" onClick={() => setOpen(true)}>
          {t("readyNotice.button")}
        </button>
      )}
      {open && (
        <div className="ready-panel">
          <h3>
            {t("readyNotice.title")} <Help id="readyNotice.send" />
          </h3>
          {preview.amount !== null && <p>{t("readyNotice.amountLine", { amount: t("readyNotice.approx", { amount: formatReadyAmount(preview.amount, workshop.currency, i18n.language) }) })}</p>}
          {noAddress && <p className="warn-text">{t("readyNotice.siteMissing")}</p>}
          <ChannelSend
            type="ready"
            customer={customer}
            buttonLabel={t("readyNotice.send")}
            prepare={async () => {
              const token = await store.createReadyNotice(order.id, preview.amount, preview.hours);
              return { token, ...texts({ order, customerName: customer?.name ?? null, gross, token }) };
            }}
            send={(channel, prepared) => {
              const p = prepared as ReadyTexts & { token: string };
              const link = readyLink(p.token);
              return store.sendReadyNotice(order.id, channel, channel === "email"
                ? { subject: p.subject, text: p.email, link, buttonLabel: t("readyNotice.emailButton") }
                : { subject: "", text: p.sms, link, buttonLabel: "" });
            }}
            onDone={() => load()}
          />
          <details className="ready-preview">
            <summary>{t("readyNotice.preview")}</summary>
            <p className="ps-cap">{t("channels.email")}: {preview.subject}</p>
            <pre className="msg-text">{preview.email}</pre>
            <p className="ps-cap">{t("readyNotice.previewSms", { count: preview.smsParts })}</p>
            <pre className="msg-text">{preview.sms}</pre>
            {preview.smsPlain && <p className="muted small">{t("readyNotice.smsPlain")}</p>}
          </details>
          <button type="button" className="link" onClick={() => setOpen(false)}>{t("common.close")}</button>
        </div>
      )}
      <ReadyNoticeList notices={notices} />
    </section>
  );
}
